/**
 * Per-2m-bar CME taker delta (buyer- minus seller-initiated volume from raw
 * SCID prints), for the candle "both agree" 🐋🦈 markers.
 *
 * Contract (utt-engine): GET {api}/cme-bar-delta/{symbol}?from=ms
 *   -> { bars: [[t, buy, sell]], ready }  (absorption tracker's bid/ask split,
 *   ~7 days after a restart). Fetched again whenever a bar closes.
 */
import { create } from 'zustand';
import { getApiBaseUrl } from '../config/ports';

interface CmeDeltaState {
  /** Base bar open (ms) -> buy - sell, per symbol as requested. */
  bySymbol: Record<string, Map<number, number>>;
  /** Per symbol: earliest `from` requested and newest bar held. */
  span: Record<string, { from: number; newest: number }>;
  version: number;
  fetchDeltas: (symbol: string, fromMs: number) => Promise<void>;
}

export const useCmeDeltaStore = create<CmeDeltaState>((set, get) => ({
  bySymbol: {},
  span: {},
  version: 0,
  fetchDeltas: async (symbol, fromMs) => {
    if (!symbol) return;
    const span = get().span[symbol];
    // Only what is not here yet: older history the chart just loaded, else
    // from the newest bar held (it may have been partial).
    let from = Math.floor(fromMs);
    if (span && from >= span.from) from = span.newest;
    try {
      const res = await fetch(`${getApiBaseUrl()}/cme-bar-delta/${encodeURIComponent(symbol)}?from=${from}`);
      if (!res.ok) return;
      const body = await res.json();
      const rows: unknown[] = Array.isArray(body?.bars) ? body.bars : [];
      if (rows.length === 0) return;
      set((s) => {
        const next = new Map(s.bySymbol[symbol] ?? []);
        let newest = s.span[symbol]?.newest ?? 0;
        for (const r of rows) {
          if (Array.isArray(r) && typeof r[0] === 'number' && typeof r[1] === 'number' && typeof r[2] === 'number') {
            next.set(r[0], r[1] - r[2]);
            newest = Math.max(newest, r[0]);
          }
        }
        const prevFrom = s.span[symbol]?.from ?? from;
        return {
          bySymbol: { ...s.bySymbol, [symbol]: next },
          span: { ...s.span, [symbol]: { from: Math.min(prevFrom, from), newest } },
          version: s.version + 1,
        };
      });
    } catch {
      /* engine without the endpoint: no CME delta, agreement never fires */
    }
  },
}));

/**
 * Sum 2m deltas into chart bars (seconds), folding like the CME candles.
 * Below 2m every 1m candle carries the delta of the 2m bar containing it.
 */
export function cmeDeltaByChartBar(deltas: ReadonlyMap<number, number> | undefined, timeframeMinutes: number): Map<number, number> {
  const out = new Map<number, number>();
  if (!deltas) return out;
  const tfMs = Math.round(timeframeMinutes * 60_000);
  if (tfMs < 120_000) {
    for (const [t, d] of deltas) {
      for (let k = t; k < t + 120_000; k += Math.max(60_000, tfMs)) out.set(Math.floor(k / 1000), d);
    }
    return out;
  }
  for (const [t, d] of deltas) {
    const key = Math.floor((Math.floor(t / tfMs) * tfMs) / 1000);
    out.set(key, (out.get(key) ?? 0) + d);
  }
  return out;
}
